/**
 * FeatureTour registry — THE single file to edit when adding a tour.
 *
 * A tour is a multi-step spotlight walkthrough scoped to one page (route).
 * Targets are located via `data-tour="<id>"` attributes on the components
 * being explained (add the attribute where needed — Chakra forwards data-*
 * props to the DOM).
 *
 * Tour contract:
 *   id                  kebab-case, unique, NO DOTS (used as a Firestore field path)
 *   version             integer; bump it to re-show a materially changed tour
 *                       to users who already completed the old one
 *   routes              array of react-router v5 path patterns (must match the
 *                       paths declared in AppRouter; matched with exact:true)
 *   title               shown in the navbar tutorial button menu
 *   autoStart           true = starts on the user's first visit to the page;
 *                       false = replay-only (navbar tutorials menu / TourReplayButton)
 *   audience            'all' (default) | 'admins' | 'non-admins' — who is
 *                       eligible for this tour. Gates BOTH auto-start and the
 *                       navbar tutorials menu. "Admin" = SuperAdmin or CRM
 *                       Admin (see isTourAdmin below).
 *   requiresNewFeatures true = only runs for canAccessNewFeatures() users
 *   menuSection         optional group title in the navbar tutorials dropdown
 *                       (e.g. 'Rate Alerts'). Tours sharing a section render
 *                       under one heading; omit on single-surface pages.
 *   menuAlso            optional array of EXTRA menu placements on OTHER
 *                       routes: { route, section?, action? }. The tour is
 *                       listed (never auto-started) on that route's dropdown;
 *                       clicking it either runs the named page action (which
 *                       must navigate to the tour's own route — required when
 *                       every tour route has a :param) or, with no action,
 *                       navigates to the tour's first route. The engine then
 *                       starts the tour once the route matches.
 *   steps               ordered array of step objects
 *
 * Step contract:
 *   target            data-tour id to spotlight (engine queries [data-tour="..."])
 *   selector          raw CSS selector escape hatch (wins over target)
 *   title / body      card copy (plain strings)
 *   placement         'top' | 'bottom' | 'left' | 'right' | 'auto' (default 'auto')
 *   spotlightPadding  px around the target, default 8
 *   interactive       true = the target stays clickable through the spotlight
 *   advanceOn         'click' = clicking the target advances to the next step
 *   action            name of a page-registered action (see useTourActions) run
 *                     BEFORE locating the target — e.g. open a modal
 *   waitForMs         how long to wait for the target before skipping the step
 *                     (default 5000)
 *
 * Missing targets: a step whose target never appears is skipped with a console
 * warning. If the FIRST step of an auto-started tour is missing, the whole
 * tour aborts silently WITHOUT being marked seen (it retries next visit) —
 * this covers role-gated targets.
 */

import { matchPath } from 'react-router-dom';

export const TOURS = [
  // ---------------------------------------------------------------------------
  // ORIGIN DASHBOARD (/crm, /crm/dashboard)
  // dashboard-overview is FIRST so it auto-starts before dashboard-add-contact
  // (registry order = auto-start priority; add-contact drips on the next visit).
  // ---------------------------------------------------------------------------
  {
    id: 'dashboard-overview',
    version: 1,
    routes: ['/crm/dashboard', '/crm'],
    title: 'Dashboard Overview',
    autoStart: true,
    requiresNewFeatures: false,
    steps: [
      {
        target: 'dashboard-header',
        title: 'Your command center',
        body: 'This is home base: every contact you own, your tasks, and your daily plan in one place. Let\u2019s take a quick lap.',
        placement: 'bottom',
        waitForMs: 8000, // page shows a full-screen spinner while contacts load
      },
      {
        // Admin-preview gate: silently skipped for users without the briefing.
        target: 'dashboard-briefing',
        title: 'Your day, pre-planned',
        body: 'FutureBot builds a daily briefing: who to call first, deals that need a push, and follow-ups about to slip. Expand it each morning before you dial.',
        placement: 'bottom',
      },
      {
        target: 'dashboard-kpis',
        title: 'The pulse',
        body: 'Active deals in your pipeline, upcoming loan anniversaries and birthdays (perfect excuses to call), and your open tasks \u2014 all live.',
        placement: 'bottom',
      },
      {
        target: 'dashboard-table',
        title: 'Your book of business',
        body: 'Every contact lives here. Click any row to open their full workspace \u2014 deals, activity, notes and campaigns \u2014 without leaving the page.',
        placement: 'top',
      },
      {
        target: 'dashboard-search',
        title: 'Find anyone in seconds',
        body: 'Type a name, email or phone and press Enter. Clears with one click when you\u2019re done.',
        placement: 'bottom',
      },
      {
        target: 'dashboard-filters',
        title: 'Slice your list',
        body: 'Filter by contact type, tags, favorites or Do-Not-Contact status \u2014 then save any combination as a Smart List you can reopen in one click.',
        placement: 'bottom',
      },
      {
        target: 'dashboard-customize',
        title: 'Make the table yours',
        body: 'Add, remove and reorder columns \u2014 show lifetime value, last contact, license states, whatever you work by. Your layout is saved automatically.',
        placement: 'bottom',
      },
      {
        target: 'dashboard-new-contact',
        title: 'Grow the book',
        body: 'Add contacts one at a time here \u2014 there\u2019s a separate quick tutorial for this button in the help menu whenever you want it.',
        placement: 'bottom',
      },
    ],
  },
  {
    id: 'dashboard-add-contact',
    version: 1,
    routes: ['/crm/dashboard', '/crm'],
    title: 'Adding Contacts',
    autoStart: true,
    requiresNewFeatures: false,
    steps: [
      {
        target: 'dashboard-new-contact',
        title: 'Add contacts manually',
        body: 'Click "New Contact" to create a contact with just a name and an email or phone. Try it now, or hit Next and we\u2019ll open it for you.',
        placement: 'bottom',
        interactive: true,
        advanceOn: 'click',
      },
      {
        target: 'qac-name',
        action: 'openAddContact',
        title: 'Who are they?',
        body: 'First and last name are required — everything else can be filled in later.',
        placement: 'bottom',
      },
      {
        target: 'qac-contact-info',
        title: 'How do you reach them?',
        body: 'Enter an email or a phone number — at least one of the two is required. Duplicates are detected automatically.',
        placement: 'right',
      },
      {
        target: 'qac-type-tags',
        title: 'Type & tags',
        body: 'Pick one or more contact types (the first one becomes the primary type). Tags are optional and can be created on the fly.',
        placement: 'right',
      },
      {
        target: 'qac-actions',
        title: 'Save it',
        body: '"Add Contact" saves and closes. "Add & Open" saves and jumps straight into the new contact\u2019s profile.',
        placement: 'top',
      },
    ],
  },
  {
    // FHL Scheduling launch nudge: one step pointing at the new sidebar item.
    // Clicking it navigates to /crm/integrations, where the setup tour below
    // picks up. Aborts silently (and retries) if the nav item isn't rendered.
    id: 'scheduling-booking-link-nudge',
    version: 1,
    routes: ['/crm/dashboard', '/crm'],
    title: 'Your Personal Booking Link',
    autoStart: true,
    requiresNewFeatures: false,
    steps: [
      {
        target: 'nav-integrations',
        title: 'New: your personal booking link',
        body: 'FHL Scheduling is live — a booking page you can text or email so borrowers pick a time straight off your calendar. Click Integrations to set yours up (about a minute).',
        placement: 'right',
        interactive: true,
        advanceOn: 'click',
      },
    ],
  },
  {
    id: 'dashboard-import-export',
    version: 1,
    routes: ['/crm/dashboard', '/crm'],
    title: 'Importing & Exporting',
    autoStart: false, // replay-only: start it from the navbar tutorials menu
    requiresNewFeatures: false,
    audience: 'admins',
    steps: [
      {
        target: 'dashboard-import',
        title: 'Bulk import',
        body: 'Import contacts in bulk from a CSV file. You\u2019ll map your columns to contact fields during the import.',
        placement: 'bottom',
      },
      {
        target: 'dashboard-export',
        title: 'Bulk export',
        body: 'Admins can export the contact list (respecting current filters) to a CSV file from here.',
        placement: 'bottom',
      },
    ],
  },
  {
    id: 'dashboard-find-contacts',
    version: 1,
    routes: ['/crm/dashboard', '/crm'],
    title: 'Finding Contacts',
    autoStart: false, // replay-only: start it from the navbar tutorials menu
    requiresNewFeatures: false,
    steps: [
      {
        target: 'dashboard-search',
        title: 'Start with search',
        body: 'Fastest path to one person: type part of their name, email or phone and press Enter.',
        placement: 'bottom',
      },
      {
        target: 'dashboard-filters',
        title: 'Filter for a group',
        body: 'Building a call list? Stack filters: contact type, tags, favorites, DNC status \u2014 or hit "More Filters" for any field in the CRM.',
        placement: 'bottom',
      },
      {
        target: 'dashboard-smart-lists',
        title: 'Save it as a Smart List',
        body: 'Once your filters are right, save them as a Smart List: "My Lists" are private, "Shared Lists" go to the whole team, and you can set one as your default view.',
        placement: 'bottom',
      },
      {
        target: 'dashboard-customize',
        title: 'See the right columns',
        body: 'Match the table to the job \u2014 add Last Contact for follow-up sweeps or Lifetime Value when you\u2019re ranking relationships.',
        placement: 'bottom',
      },
    ],
  },
  {
    id: 'dashboard-bulk-actions',
    version: 1,
    routes: ['/crm/dashboard', '/crm'],
    title: 'Bulk Actions',
    autoStart: false, // replay-only: start it from the navbar tutorials menu
    requiresNewFeatures: false,
    steps: [
      {
        target: 'dashboard-table',
        title: 'Select in bulk',
        body: 'Tick the checkbox on any rows \u2014 or the header checkbox for the whole page. A banner then offers to select EVERY contact matching your filters.',
        placement: 'top',
      },
      {
        target: 'dashboard-bulk-bar',
        action: 'selectFirstContact', // seeds a selection so the bar exists
        title: 'Act on all of them at once',
        body: 'The bar appears whenever contacts are selected: send a bulk email or text, update tags or types, transfer ownership, export (admins) or delete. "Clear" deselects everything.',
        placement: 'top',
        spotlightPadding: 12,
      },
    ],
  },
  // ---------------------------------------------------------------------------
  // INTEGRATIONS (/crm/integrations)
  // ---------------------------------------------------------------------------
  {
    id: 'integrations-scheduling-setup',
    version: 1,
    routes: ['/crm/integrations'],
    title: 'FHL Scheduling Setup',
    autoStart: true,
    requiresNewFeatures: false,
    steps: [
      {
        target: 'integration-card-scheduling',
        title: 'Meet FHL Scheduling',
        body: 'Your own booking page at fhl.one \u2014 borrowers pick a time, it lands on your Google Calendar with a Meet link, and the meeting logs itself in Origin with automatic reminders.',
        placement: 'bottom',
      },
      {
        target: 'integration-panel',
        title: 'Two steps, one minute',
        body: 'Connect Google Calendar, then click "Create my scheduling account". You\u2019ll get a ready-to-share booking link \u2014 plus meeting types, availability and bookings, all managed right here.',
        placement: 'top',
      },
    ],
  },
  // ---------------------------------------------------------------------------
  // MY DEALS (/crm/my-deals)
  // ---------------------------------------------------------------------------
  {
    id: 'my-deals-overview',
    version: 1,
    routes: ['/crm/my-deals'],
    title: 'My Deals Overview',
    autoStart: true,
    requiresNewFeatures: false,
    steps: [
      {
        target: 'deals-header',
        title: 'Your pipeline HQ',
        body: 'Every deal you\u2019re working, plus the buttons you\u2019ll use most: Add Task and Add Lead (\u2318N works too).',
        placement: 'bottom',
        waitForMs: 8000, // page shows a full-screen spinner while deals load
      },
      {
        target: 'deals-stats',
        title: 'Read the room in five numbers',
        body: 'Pipeline size, tasks due today, overdue tasks, at-risk (stagnant) deals and your conversion rate. Red numbers = start there.',
        placement: 'bottom',
      },
      {
        target: 'deals-table',
        title: 'Work the list',
        body: 'Click any row (or its "Work" button) to open the full deal workspace. Lost deals are hidden by default \u2014 flip "Hide Lost" in the filters to see them.',
        placement: 'top',
      },
      {
        target: 'deals-search',
        title: 'Jump to a deal',
        body: 'Search by borrower name, email, phone or loan number, then hit Search or press Enter.',
        placement: 'bottom',
      },
      {
        target: 'deals-filters',
        title: 'Focus the pipeline',
        body: 'Filter by stage, tags, at-risk or DNC \u2014 and save any view as a Smart List. Great for a "hot leads only" morning list.',
        placement: 'bottom',
      },
      {
        target: 'deals-add-task',
        title: 'Never lose a follow-up',
        body: 'Create a standalone task with a due date. It shows up here, on your dashboard, and in your daily plan.',
        placement: 'bottom',
      },
      {
        target: 'deals-add-lead',
        title: 'New lead in seconds',
        body: 'Add Lead creates the contact AND the deal in one shot. There\u2019s a dedicated walkthrough for it in the help menu.',
        placement: 'bottom',
      },
    ],
  },
  {
    id: 'my-deals-add-lead',
    version: 1,
    routes: ['/crm/my-deals'],
    title: 'Adding a Lead',
    autoStart: false, // replay-only: start it from the navbar tutorials menu
    requiresNewFeatures: false,
    steps: [
      {
        target: 'deals-add-lead',
        title: 'One button, contact + deal',
        body: 'Click "Add Lead" now \u2014 or hit Next and we\u2019ll open it for you. Either way it creates the contact and a new deal together.',
        placement: 'bottom',
        interactive: true,
        advanceOn: 'click',
      },
      {
        target: 'lead-name',
        action: 'openAddLead',
        title: 'Who\u2019s the borrower?',
        body: 'First and last name are all that\u2019s required to get the lead in the door.',
        placement: 'right',
      },
      {
        target: 'lead-contact',
        title: 'How do you reach them?',
        body: 'Add an email or a phone number \u2014 at least one of the two is required.',
        placement: 'right',
      },
      {
        // Admin-only field: silently skipped for loan officers.
        target: 'lead-assign',
        title: 'Route it (admins)',
        body: 'Assign the lead to a loan officer now, or leave it blank to drop it in the unassigned queue for round-robin pickup.',
        placement: 'right',
      },
      {
        target: 'lead-loan',
        title: 'Loan details (all optional)',
        body: 'Product, amount, purchase vs. refi, property address and lead source. Skip anything you don\u2019t know yet \u2014 you can fill it in on the deal later.',
        placement: 'right',
      },
      {
        target: 'lead-submit',
        title: 'Send it',
        body: '"Add Lead" creates the deal at the New Lead stage and takes you back to the pipeline. Your speed-to-lead clock starts now \u2014 go call them!',
        placement: 'top',
      },
    ],
  },
  {
    id: 'my-deals-bulk',
    version: 1,
    routes: ['/crm/my-deals'],
    title: 'Bulk Deal Updates',
    autoStart: false, // replay-only: start it from the navbar tutorials menu
    requiresNewFeatures: false,
    steps: [
      {
        target: 'deals-table',
        title: 'Select some deals',
        body: 'Tick the checkboxes on the rows you want to update \u2014 or the header checkbox to grab the whole page.',
        placement: 'top',
      },
      {
        target: 'deals-bulk-bar',
        action: 'selectFirstDeal', // seeds a selection so the bar exists
        title: 'Update them together',
        body: '"Change Stage" moves every selected deal at once \u2014 the fastest way to advance stages in bulk (single deals move on their own deal page). You can also tag, reassign or delete from here.',
        placement: 'top',
        spotlightPadding: 12,
      },
    ],
  },
  // ---------------------------------------------------------------------------
  // MORTGAGE MONITOR (/crm/mortgage-monitor) — behind the new-features gate
  // ---------------------------------------------------------------------------
  {
    id: 'monitor-overview',
    version: 1,
    routes: ['/crm/mortgage-monitor'],
    menuSection: 'Opportunities',
    title: 'Mortgage Monitor Overview',
    autoStart: true,
    requiresNewFeatures: true, // widens to everyone automatically at GA
    steps: [
      {
        target: 'monitor-header',
        title: 'Your refi radar',
        body: 'Every funded client is checked against today\u2019s market daily. When their rate is far enough above market, they show up here \u2014 so you can call before another lender does.',
        placement: 'bottom',
        waitForMs: 8000,
      },
      {
        target: 'monitor-stats',
        title: 'The opportunity at a glance',
        body: 'How many clients are in the money, what they\u2019d save each month, and what those refis are worth to you in comp.',
        placement: 'bottom',
      },
      {
        target: 'monitor-rates',
        title: 'Today\u2019s market',
        body: 'Live rates with a 30-day trend for each product. These are the benchmarks every funded loan is compared against.',
        placement: 'bottom',
      },
      {
        target: 'monitor-status-tabs',
        title: 'Work the pipeline left to right',
        body: 'Open = untouched opportunities. Mark them Contacted after the call, Enrolled once they\u2019re in a nurture campaign, or Dismissed if it\u2019s a no. Below Threshold shows near-misses.',
        placement: 'bottom',
      },
      {
        target: 'monitor-filters',
        title: 'Narrow the list',
        body: 'Filter by loan type, term, savings, balance or equity \u2014 handy when you only want the biggest wins on the screen.',
        placement: 'bottom',
      },
      {
        target: 'monitor-search',
        title: 'Find one borrower',
        body: 'Search by borrower name, city, or loan officer.',
        placement: 'bottom',
      },
      {
        target: 'monitor-table',
        title: 'The money list',
        body: 'Sorted by monthly savings by default. Each row\u2019s \u22ee menu lets you mark contacted, enroll them in the refi nurture campaign, open the deal in Origin or Arive, or dismiss. Click a row for full loan math.',
        placement: 'top',
      },
    ],
  },
  {
    id: 'monitor-rate-alerts',
    version: 1,
    routes: ['/crm/mortgage-monitor'],
    menuSection: 'Rate Alerts',
    title: 'Rate Alert Emails',
    autoStart: false, // replay-only: start it from the navbar tutorials menu
    requiresNewFeatures: true,
    steps: [
      {
        target: 'monitor-tab-alerts',
        title: 'Alerts come to you',
        body: 'The Rate Alerts tab is your personal email digest of in-the-money clients \u2014 no need to check this page every day. Hit Next and we\u2019ll open it.',
        placement: 'bottom',
      },
      {
        target: 'alerts-master',
        action: 'showAlertsTab',
        title: 'Turn it on, pick a rhythm',
        body: 'Enable alerts, choose daily or weekly delivery, and decide whether repeats are skipped ("Only new opportunities") or every match is included. Equity alerts flag cash-out angles too.',
        placement: 'bottom',
        waitForMs: 8000, // tab shows skeletons while alert settings load
      },
      {
        target: 'alerts-criteria',
        title: 'Your bar for "worth emailing"',
        body: 'Per loan type: minimum rate drop, monthly savings and balance range. Blank fields inherit the monitor\u2019s global thresholds \u2014 fill in only what you want to override.',
        placement: 'top',
      },
      {
        target: 'alerts-save',
        title: 'Save, then prove it works',
        body: '"Save alert settings" locks it in, and "Send me a test now" delivers a real digest of current matches to your inbox so you can see exactly what you\u2019ll get.',
        placement: 'top',
      },
    ],
  },
  {
    id: 'monitor-admin',
    version: 1,
    routes: ['/crm/mortgage-monitor'],
    menuSection: 'Admin',
    title: 'Monitor Admin Controls',
    autoStart: false, // replay-only: start it from the navbar tutorials menu
    audience: 'admins',
    requiresNewFeatures: true,
    steps: [
      {
        target: 'monitor-view-as',
        title: 'See any LO\u2019s book',
        body: 'Switch the whole page \u2014 opportunities, stats, and the Rate Alerts tab \u2014 to any loan officer. Great for coaching or covering while someone\u2019s out.',
        placement: 'bottom',
      },
      {
        target: 'monitor-admin-btn',
        title: 'The engine room',
        body: 'The Admin drawer holds the manual controls and all monitor-wide settings. Hit Next to open it.',
        placement: 'bottom',
      },
      {
        target: 'monitor-admin-rates',
        action: 'openMonitorAdmin',
        title: 'Refresh rates on demand',
        body: 'Rates auto-fetch every morning, but if the market moves midday you can pull fresh numbers now.',
        placement: 'left',
      },
      {
        target: 'monitor-admin-scan',
        title: 'Re-scan the book',
        body: 'Re-runs every funded loan against current rates immediately \u2014 do this after fetching new rates or changing thresholds.',
        placement: 'left',
      },
      {
        target: 'monitor-admin-backfill',
        title: 'Keep the mirror complete',
        body: 'Pulls funded loans missing from the mirror and enriches thin rows with note rate and key dates. Preview with dry-run first, then run for real until "remaining" hits zero.',
        placement: 'left',
      },
      {
        target: 'monitor-admin-settings',
        title: 'The rules of the game',
        body: 'Global thresholds (rate delta, savings, balance), LO comp bps for the projections, equity flags and per-loan-type overrides. Save here, then re-scan to apply.',
        placement: 'left',
      },
    ],
  },
  // ---------------------------------------------------------------------------
  // LEADERBOARD (/crm/leaderboard) — behind the new-features gate
  // ---------------------------------------------------------------------------
  {
    id: 'leaderboard-overview',
    version: 1,
    routes: ['/crm/leaderboard'],
    title: 'Leaderboard Overview',
    autoStart: true,
    requiresNewFeatures: true, // widens to everyone automatically at GA
    steps: [
      {
        target: 'lb-header',
        title: 'Company production, live',
        body: 'Funded volume straight from the Arive book \u2014 no self-reporting. This is the scoreboard for the whole shop.',
        placement: 'bottom',
        waitForMs: 8000,
      },
      {
        target: 'lb-period',
        title: 'Pick your window',
        body: 'This month, last month, quarter, year-to-date \u2014 or any single completed month this year.',
        placement: 'bottom',
      },
      {
        target: 'lb-totals',
        title: 'Team totals',
        body: 'Company funded volume, units, and how many LOs put points on the board this period.',
        placement: 'bottom',
      },
      {
        // Renders only when the period has production — skipped when empty.
        target: 'lb-podium',
        title: 'The podium',
        body: 'Top three producers for the period. Your spot is highlighted when you\u2019re up here.',
        placement: 'bottom',
      },
      {
        target: 'lb-rankings',
        title: 'Full rankings',
        body: 'Everyone with production, sortable by volume, units or average loan size. Your row is highlighted and badged with your rank.',
        placement: 'top',
      },
      {
        target: 'lb-pc-card',
        title: 'President\u2019s Club race',
        body: 'Yearly targets \u2014 qualify on volume OR units. "ON PACE" means you\u2019d make it at your current run rate.',
        placement: 'left',
      },
      {
        target: 'lb-club-card',
        title: 'The volume milestone club',
        body: 'Cross the YTD volume milestone and earn extra comp bps on everything after. Shows who\u2019s in and who\u2019s closest to the door.',
        placement: 'left',
      },
      {
        target: 'lb-review-card',
        title: 'Your private coaching review',
        body: 'A monthly AI-written review of YOUR numbers \u2014 funded, speed-to-lead, conversation rate vs. team quartiles. Nobody else can see it. Generate or regenerate any time.',
        placement: 'left',
      },
    ],
  },
  {
    id: 'leaderboard-admin',
    version: 1,
    routes: ['/crm/leaderboard'],
    title: 'Leaderboard Admin',
    autoStart: false, // replay-only: start it from the navbar tutorials menu
    audience: 'admins',
    requiresNewFeatures: true,
    steps: [
      {
        target: 'lb-view-as',
        title: 'View as any LO',
        body: 'Flip the page to a loan officer\u2019s perspective: their highlight, their rank badge, and their private coaching review (marked as admin view).',
        placement: 'bottom',
      },
      {
        target: 'lb-admin-btn',
        title: 'Targets & exclusions',
        body: 'The Admin modal sets the club targets and hides leadership accounts from the board. Hit Next to open it.',
        placement: 'bottom',
      },
      {
        target: 'lb-admin-pc',
        action: 'openLeaderboardAdmin',
        title: 'President\u2019s Club targets',
        body: 'Annual volume and units targets \u2014 LOs qualify by hitting either one. These also power the Presidents Club card on the Admin page.',
        placement: 'right',
      },
      {
        target: 'lb-admin-club',
        title: 'Milestone club settings',
        body: 'The YTD volume milestone and the comp bonus (bps) an LO earns for crossing it. Renaming the target updates the card title automatically.',
        placement: 'right',
      },
      {
        target: 'lb-admin-exclusions',
        title: 'Keep the board honest',
        body: 'Excluded users vanish from rankings, podium, wins, clubs AND the team quartiles behind coaching reviews. Use it for leadership accounts holding inherited books.',
        placement: 'right',
      },
    ],
  },
  // ---------------------------------------------------------------------------
  // ANALYTICS (/crm/analytics)
  // ---------------------------------------------------------------------------
  {
    id: 'analytics-overview',
    version: 1,
    routes: ['/crm/analytics'],
    menuSection: 'Overview',
    title: 'Analytics Overview',
    autoStart: true,
    requiresNewFeatures: false,
    steps: [
      {
        target: 'analytics-header',
        title: 'Know your numbers',
        body: 'Team Analytics turns CRM activity into answers: where leads come from, how fast they\u2019re worked, and where deals stall.',
        placement: 'bottom',
        waitForMs: 8000,
      },
      {
        target: 'analytics-daterange',
        title: 'Set the window',
        body: 'Every number on this page respects this date range \u2014 presets or a fully custom start and end.',
        placement: 'bottom',
      },
      {
        target: 'analytics-filters',
        title: 'Slice by team, stage, source, tag',
        body: 'Stack filters to answer specific questions, like "how are Zillow leads converting for these two LOs?"',
        placement: 'bottom',
      },
      {
        target: 'analytics-refresh',
        title: 'Pull fresh data',
        body: 'Numbers load once per visit \u2014 hit Refresh after making changes elsewhere in the CRM.',
        placement: 'bottom',
      },
      {
        target: 'analytics-tabs',
        title: 'Five reports, one page',
        body: 'Overview for the big picture, User Activity for effort, Task and Lead Health for risk, and the Monday Report for the weekly leadership rundown.',
        placement: 'bottom',
      },
      {
        target: 'analytics-kpis',
        title: 'The headline numbers',
        body: 'Lead volume and pipeline counts up top, with conversion rate, deal value and response times in the row below.',
        placement: 'bottom',
      },
      {
        target: 'analytics-funnel',
        title: 'Where leads leak',
        body: 'The conversion funnel shows drop-off between stages \u2014 a thin bar means that hand-off needs attention.',
        placement: 'top',
      },
      {
        target: 'analytics-sources',
        title: 'Which sources earn their keep',
        body: 'Leads and conversions by source. Click a bar to drill into the exact leads behind it.',
        placement: 'top',
      },
      {
        target: 'analytics-lo-perf',
        title: 'Production by loan officer',
        body: 'Leads, wins, conversion and value per LO for the selected window \u2014 the coaching conversation starter.',
        placement: 'top',
      },
    ],
  },
  {
    id: 'analytics-health',
    version: 1,
    routes: ['/crm/analytics'],
    menuSection: 'Health Tabs',
    title: 'Task & Lead Health',
    autoStart: false, // replay-only: start it from the navbar tutorials menu
    requiresNewFeatures: false,
    steps: [
      {
        target: 'analytics-task-kpis',
        action: 'showTaskHealthTab',
        title: 'Task discipline at a glance',
        body: 'Deals without tasks and deals with overdue tasks are your risk list. These cards are clickable \u2014 each opens the exact deals behind the number.',
        placement: 'bottom',
      },
      {
        target: 'analytics-task-table',
        title: 'Who\u2019s keeping up',
        body: 'Per-user task load, completion and overdue counts \u2014 includes tasks not linked to any deal.',
        placement: 'top',
      },
      {
        target: 'analytics-lead-kpis',
        action: 'showLeadHealthTab',
        title: 'Lead risk radar',
        body: 'Stagnant leads, leads with no follow-up scheduled, and average speed-to-lead (business hours). Click the cards to see the actual leads.',
        placement: 'bottom',
      },
      {
        target: 'analytics-stagnant',
        title: 'The rescue list',
        body: 'Leads that have gone quiet: 7+ days for lead stages, 14+ for pipeline. Work this list before they go cold for good.',
        placement: 'top',
      },
    ],
  },
  {
    id: 'analytics-monday',
    version: 1,
    routes: ['/crm/analytics'],
    menuSection: 'Monday Report',
    title: 'Monday Report',
    autoStart: false, // replay-only: start it from the navbar tutorials menu
    requiresNewFeatures: false,
    steps: [
      {
        target: 'analytics-monday-kpis',
        action: 'showMondayTab',
        title: 'The weekly scorecard',
        body: 'Tracks the cohort of leads CREATED in the date range: speed-to-lead, contact rate, closings and losses. Every card drills down to the underlying leads.',
        placement: 'bottom',
      },
      {
        // Admin-only buttons: silently skipped for non-admins.
        target: 'analytics-monday-share',
        title: 'Ship it to leadership',
        body: '"Copy for Email" puts a formatted version on your clipboard; "Send Report Email" delivers it directly \u2014 perfect for the Monday morning huddle.',
        placement: 'bottom',
      },
    ],
  },
  // ---------------------------------------------------------------------------
  // SETTINGS (/crm/settings)
  // ---------------------------------------------------------------------------
  {
    id: 'settings-overview',
    version: 1,
    routes: ['/crm/settings'],
    menuSection: 'My Settings',
    title: 'Settings Tour',
    autoStart: true,
    requiresNewFeatures: false,
    steps: [
      {
        target: 'settings-header',
        title: 'Make the CRM work like you do',
        body: 'Integrations and preferences all live here. A few minutes of setup pays off every day.',
        placement: 'bottom',
        waitForMs: 8000, // page shows a full-screen spinner while settings load
      },
      {
        target: 'settings-tabs',
        title: 'One tab per topic',
        body: 'Calendar and meeting providers \u2014 and admins see extra tabs for team defaults, Arive, notifications, and field mappings.',
        placement: 'bottom',
      },
      {
        target: 'settings-calendar',
        action: 'showCalendarTab',
        title: 'Sync your calendar',
        body: 'Connect Google Calendar for true 2-way sync \u2014 meetings, events, and dated tasks push to Google in seconds, and anything you change in Google flows back into Origin automatically.',
        placement: 'top',
      },
      {
        target: 'settings-meeting',
        action: 'showMeetingTab',
        title: 'Your meeting link',
        body: 'Connect Zoom or Google Meet so scheduled meetings automatically carry a join link.',
        placement: 'top',
      },
    ],
  },
  {
    id: 'settings-admin',
    version: 1,
    routes: ['/crm/settings'],
    menuSection: 'Admin',
    title: 'Admin Settings Tools',
    autoStart: false, // replay-only: start it from the navbar tutorials menu
    audience: 'admins',
    requiresNewFeatures: false,
    steps: [
      {
        target: 'settings-admin-picker',
        title: 'Edit anyone\u2019s settings',
        body: 'Pick a user and every tab below edits THEIR settings \u2014 onboard a new LO\u2019s integrations without touching their laptop.',
        placement: 'bottom',
      },
      {
        target: 'settings-defaultview',
        action: 'showDefaultViewTab',
        title: 'Set their default lens',
        body: 'Choose whether an admin\u2019s dashboard and My Deals open scoped to "All Users" or just themselves.',
        placement: 'top',
      },
      {
        // The Arive LOS tab is temporarily admin-only, so its walkthrough
        // lives here instead of settings-overview.
        target: 'settings-arive',
        action: 'showAriveTab',
        title: 'Link Arive accounts',
        body: 'Per-user Arive API credentials and pipeline import. This tab is admin-only for now \u2014 paste keys here for yourself or for whichever LO you\u2019ve selected above.',
        placement: 'top',
      },
      {
        target: 'settings-arive-admin',
        action: 'showAriveTab',
        title: 'Arive admin panel',
        body: 'Company-level Arive controls: sync queue health, echo suppression and manual tools. Per-LO credentials stay in the section above; sync RULES live on the Admin page.',
        placement: 'top',
      },
      {
        // The Notifications tab is temporarily admin-only, so its walkthrough
        // lives here instead of settings-overview.
        target: 'settings-notifications',
        action: 'showNotificationsTab',
        title: 'Notification preferences',
        body: 'Email and in-app notifications per event type \u2014 new leads, task reminders, campaign activity. Admin-only for now; set them for yourself or any selected LO.',
        placement: 'top',
      },
      {
        target: 'settings-mappings',
        action: 'showMappingsTab',
        title: 'Field mappings',
        body: 'How Arive fields translate into Origin fields. Change a mapping here and every future sync follows it \u2014 no code needed.',
        placement: 'top',
      },
    ],
  },
  // ---------------------------------------------------------------------------
  // ADMIN PAGE (/crm/admin) — SuperAdmin route; all tours admin-audience
  // ---------------------------------------------------------------------------
  {
    id: 'admin-overview',
    version: 1,
    routes: ['/crm/admin'],
    menuSection: 'Overview',
    title: 'Admin Overview',
    autoStart: true,
    audience: 'admins',
    requiresNewFeatures: false,
    steps: [
      {
        target: 'admin-header',
        title: 'The control room',
        body: 'Everything team-wide lives here: users, lead routing, imports, templates and CRM configuration. Let\u2019s hit the tabs you\u2019ll use most.',
        placement: 'bottom',
        waitForMs: 8000, // page shows a full-screen spinner while admin data loads
      },
      {
        target: 'admin-stats',
        title: 'Health check',
        body: 'User count, licensed-state coverage \u2014 and most importantly, how many leads are sitting unassigned right now.',
        placement: 'bottom',
      },
      {
        target: 'admin-tabs',
        title: 'Fourteen tools, one strip',
        body: 'Each tab is a self-contained admin tool. We\u2019ll visit the big three now; the Config, Automation and Integrations walkthroughs in the help menu cover the rest.',
        placement: 'bottom',
      },
      {
        target: 'admin-users-table',
        title: 'Your roster',
        body: 'Every CRM user with their role, licensed states and Dialpad capability. Edit a user to change what they can see and do.',
        placement: 'top',
      },
      {
        target: 'admin-unassigned',
        action: 'showUnassignedTab',
        title: 'The routing queue',
        body: 'New leads without an owner land here (\u2318U jumps straight in). Select rows and assign them \u2014 speed matters most on this screen.',
        placement: 'top',
      },
      {
        target: 'admin-import-btn',
        action: 'showImportTab',
        title: 'Bulk import',
        body: 'Import contacts from CSV with column mapping, validation, dedupe and owner assignment. Import history is tracked below the button.',
        placement: 'bottom',
      },
    ],
  },
  {
    id: 'admin-config',
    version: 1,
    routes: ['/crm/admin'],
    menuSection: 'Configuration',
    title: 'CRM Configuration',
    autoStart: false, // replay-only: start it from the navbar tutorials menu
    audience: 'admins',
    requiresNewFeatures: false,
    steps: [
      {
        target: 'admin-config-tags',
        action: 'showConfigTab',
        title: 'One tag namespace',
        body: 'Tags are shared between contacts and deals \u2014 create, rename, recolor or merge them here and every table, filter and campaign trigger follows.',
        placement: 'top',
      },
      {
        target: 'admin-config-stages',
        title: 'Your pipeline\u2019s spine',
        body: 'Deal stages power the pipeline, stage-change automations and analytics funnels. Reorder or rename carefully \u2014 campaigns can trigger on these.',
        placement: 'top',
      },
      {
        target: 'admin-config-pc',
        title: 'President\u2019s Club targets',
        body: 'The annual volume and units targets behind the Leaderboard\u2019s club card. (The Leaderboard\u2019s own Admin modal edits the same values.)',
        placement: 'top',
      },
    ],
  },
  {
    id: 'admin-automation',
    version: 1,
    routes: ['/crm/admin'],
    menuSection: 'Automation',
    title: 'Automation & Routing',
    autoStart: false, // replay-only: start it from the navbar tutorials menu
    audience: 'admins',
    requiresNewFeatures: false,
    steps: [
      {
        target: 'admin-auto-assign',
        action: 'showAutoTab',
        title: 'Hands-off lead routing',
        body: 'Campaign send windows, weekend coverage and round-robin assignment \u2014 the three dials that decide who gets what lead, and when messages go out.',
        placement: 'top',
      },
      {
        target: 'admin-templates',
        action: 'showTemplatesTab',
        title: 'Say it once',
        body: 'Team-wide email and SMS templates with dynamic variables. Campaigns and quick-sends both pull from this library.',
        placement: 'top',
      },
      {
        target: 'admin-stage-workflow',
        action: 'showStageWorkflowTab',
        title: 'Stage-driven playbooks',
        body: 'Attach automatic actions to stage changes \u2014 tasks to create, emails to send \u2014 so process happens without anyone remembering it.',
        placement: 'top',
      },
      {
        target: 'admin-hotlead',
        action: 'showHotLeadTab',
        title: 'Hot Lead Queue',
        body: 'The escalation chain for brand-new leads: who gets pinged, in what order, and how fast \u2014 your speed-to-lead insurance policy.',
        placement: 'top',
      },
    ],
  },
  {
    id: 'admin-integrations',
    version: 1,
    routes: ['/crm/admin'],
    menuSection: 'Integrations',
    title: 'Integrations Admin',
    autoStart: false, // replay-only: start it from the navbar tutorials menu
    audience: 'admins',
    requiresNewFeatures: false,
    steps: [
      {
        target: 'admin-dialpad',
        action: 'showDialpadTab',
        title: 'Dialpad wiring',
        body: 'Grant per-user calling capability and manage the webhooks that log calls and texts back onto contact timelines.',
        placement: 'top',
      },
      {
        target: 'admin-arive-rules',
        action: 'showAriveRulesTab',
        title: 'Arive sync rules',
        body: 'Field-level rules for what syncs between Origin and Arive and in which direction. (User API credentials live in Settings \u2192 Arive LOS \u2014 not here.)',
        placement: 'top',
      },
    ],
  },
  // ---------------------------------------------------------------------------
  // CAMPAIGNS (/crm/campaigns + builder + analytics routes)
  // ---------------------------------------------------------------------------
  {
    id: 'campaigns-my',
    version: 1,
    routes: ['/crm/campaigns'],
    menuSection: 'My Campaigns',
    title: 'Campaigns Overview',
    autoStart: true,
    requiresNewFeatures: false,
    steps: [
      {
        target: 'camp-header',
        title: 'Marketing on autopilot',
        body: 'Campaigns are pre-built sequences \u2014 emails, texts, tasks \u2014 that run for the contacts you enroll. Activate once, and they work every lead the same way, every time.',
        placement: 'bottom',
        waitForMs: 8000,
      },
      {
        target: 'camp-my-section',
        title: 'Campaigns you\u2019re running',
        body: 'Each row is a campaign you\u2019ve activated: the switch pauses or resumes it, Logs shows exactly what it sent and to whom, and the gear opens its settings.',
        placement: 'bottom',
      },
      {
        target: 'camp-available-section',
        title: 'The library',
        body: 'Campaigns you haven\u2019t activated yet \u2014 company ones plus any personal campaigns you\u2019ve built. Hit Activate to start one. To enroll a specific person, use the Campaigns tab on their deal or contact page.',
        placement: 'top',
      },
    ],
  },
  {
    id: 'campaigns-manage',
    version: 2, // v2: builder opened to all users; admins choose company vs personal
    routes: ['/crm/campaigns'],
    menuSection: 'Admin',
    title: 'Managing Templates',
    autoStart: false, // replay-only: start it from the navbar tutorials menu
    audience: 'admins',
    requiresNewFeatures: false,
    steps: [
      {
        target: 'camp-new',
        title: 'Build a new campaign',
        body: 'New Campaign opens the builder \u2014 every user can build personal campaigns now. As an admin you choose whether yours is a company campaign (everyone\u2019s library) or personal (just you).',
        placement: 'bottom',
      },
      {
        target: 'camp-admin-table',
        action: 'showManageTab',
        title: 'Company templates, one table',
        body: 'Manage subscriber counts, visibility and status for company campaigns. Users\u2019 personal campaigns stay private \u2014 impersonate a user (\u201CView as user\u201D) to see or edit theirs. Manage opens per-campaign controls; Logs shows its full send history.',
        placement: 'top',
      },
    ],
  },
  {
    id: 'campaign-builder',
    version: 2, // v2: builder open to all users (personal campaigns)
    routes: ['/crm/campaigns/builder', '/crm/campaigns/builder/:templateId'],
    // No menuAlso on /crm/campaigns: the builder needs a draft/template on
    // screen for its targets, so it only makes sense once you're actually in it.
    title: 'Campaign Builder',
    autoStart: false, // replay-only: start it from the navbar tutorials menu
    audience: 'all',
    requiresNewFeatures: false,
    steps: [
      {
        target: 'builder-name',
        title: 'Name it, describe it',
        body: 'Name and description are what LOs see in the campaign library \u2014 make them say WHO it\u2019s for. "Allow re-enrollment" lets the same contact trigger it more than once.',
        placement: 'bottom',
        waitForMs: 8000, // builder shows a spinner while a template loads
      },
      {
        target: 'builder-trigger',
        title: 'When does it fire?',
        body: 'The trigger starts the sequence: enrollment, a stage change, a tag being added, a form submission and more. Click it to configure.',
        placement: 'bottom',
      },
      {
        target: 'builder-add-node',
        title: 'Build the sequence',
        body: 'Each + inserts a step: email, SMS, wait, task, branch (if/else), tag or stage change. Steps run top to bottom.',
        placement: 'bottom',
      },
      {
        target: 'builder-mode-toggle',
        title: 'Two views, same campaign',
        body: 'Simple is a linear list \u2014 fastest for most edits. Canvas is a visual flowchart that makes branching easier to see.',
        placement: 'bottom',
      },
      {
        target: 'builder-test',
        title: 'Dry-run before launch',
        body: 'Pick a test contact and hit Test All: every step executes in preview mode so you can read the exact emails and texts it would send.',
        placement: 'bottom',
      },
      {
        target: 'builder-import-export',
        title: 'Campaigns as JSON',
        body: 'Export copies the whole workflow to your clipboard as JSON; Import pastes one back in. Great for backups or cloning a campaign.',
        placement: 'bottom',
      },
      {
        target: 'builder-save',
        title: 'Ship it',
        body: 'Save publishes your changes and subscribes you to a new campaign. Personal campaigns are yours alone; company campaigns (admin-built) appear in everyone\u2019s library.',
        placement: 'bottom',
      },
    ],
  },
  {
    id: 'campaigns-analytics',
    version: 1,
    routes: ['/crm/campaigns/analytics'],
    // Also listed on the campaigns list page — navigates to the analytics page.
    menuAlso: [{ route: '/crm/campaigns', section: 'My Campaigns' }],
    title: 'Campaign Analytics',
    autoStart: true,
    requiresNewFeatures: false,
    steps: [
      {
        target: 'campstats-cards',
        title: 'Is the machine working?',
        body: 'Total campaigns, active subscribers, steps completed and \u2014 the number that matters \u2014 funded volume attributed to campaigns.',
        placement: 'bottom',
        waitForMs: 8000, // page shows a spinner while stats load
      },
      {
        target: 'campstats-table',
        title: 'Per-campaign scoreboard',
        body: 'Subscribers, completion and success rate for every campaign. A low success rate usually means a broken step \u2014 check that campaign\u2019s logs.',
        placement: 'top',
      },
    ],
  },
  // ---------------------------------------------------------------------------
  // MY MARKETING (/crm/my-marketing + newsletter editor route)
  // ---------------------------------------------------------------------------
  {
    id: 'marketing-overview',
    version: 1,
    routes: ['/crm/my-marketing'],
    menuSection: 'Overview',
    title: 'My Marketing Overview',
    autoStart: true,
    requiresNewFeatures: false,
    steps: [
      {
        target: 'mm-header',
        title: 'Your marketing headquarters',
        body: 'Everything you send — newsletters, campaign automations, bulk email — is tracked here in one place, with the engagement numbers to prove what\u2019s working.',
        placement: 'bottom',
      },
      {
        target: 'mm-new-newsletter',
        title: 'The main event',
        body: 'A recurring realtor newsletter is the highest-leverage marketing you can run: set it up once and it sends itself on schedule, from your own Gmail.',
        placement: 'bottom',
      },
      {
        target: 'mm-tabs',
        title: 'Four views',
        body: 'Overview for the numbers, Newsletters to manage your series, Templates for ready-made company content, and Activity for the send-by-send log.',
        placement: 'bottom',
      },
      {
        target: 'mm-kpis',
        action: 'showOverviewTab',
        title: 'Your 30-day scorecard',
        body: 'Emails sent, open and click rates, unsubscribes, and your emailable realtor audience. Hover any card for exactly what it counts.',
        placement: 'bottom',
      },
      {
        target: 'mm-chart',
        title: 'The trend line',
        body: 'Twelve weeks of sends, opens and clicks side by side — a quick read on whether your marketing rhythm is building or slipping.',
        placement: 'top',
      },
      {
        target: 'mm-campaigns-card',
        title: 'Campaigns at a glance',
        body: 'Every campaign you\u2019re enrolled in, with pause/resume switches right here — no trip to the Campaigns page needed.',
        placement: 'top',
      },
      {
        target: 'mm-recent-issues',
        title: 'Latest issues',
        body: 'Your most recent newsletter sends with open rates. Click View all for full issue history per newsletter.',
        placement: 'top',
      },
    ],
  },
  {
    id: 'marketing-newsletters',
    version: 1,
    routes: ['/crm/my-marketing'],
    menuSection: 'Newsletters',
    title: 'Managing Newsletters',
    autoStart: false, // replay-only: start it from the navbar tutorials menu
    requiresNewFeatures: false,
    steps: [
      {
        target: 'mm-newsletters-list',
        action: 'showNewslettersTab',
        title: 'Your newsletter series',
        body: 'Each card is one recurring newsletter: its status, audience size, cadence, next send and last issue\u2019s open rate. The switch pauses or resumes the schedule; the \u22ee menu has Edit, Send now, Send me a test, Issue history and Archive.',
        placement: 'bottom',
      },
      {
        target: 'mm-new-newsletter',
        title: 'Start another series',
        body: 'You can run several newsletters at once — say, a monthly market update and a quarterly co-marketing pitch, each with its own audience and schedule. The editor opens with its own walkthrough.',
        placement: 'bottom',
      },
    ],
  },
  {
    id: 'marketing-templates',
    version: 1,
    routes: ['/crm/my-marketing'],
    menuSection: 'Templates',
    title: 'Using Company Templates',
    autoStart: false, // replay-only: start it from the navbar tutorials menu
    requiresNewFeatures: false,
    steps: [
      {
        // Skip-safe: the note only renders when templates exist.
        target: 'mm-templates-note',
        action: 'showTemplatesTab',
        title: 'Two ways to use a template',
        body: 'Stay synced means every send pulls the template\u2019s LATEST version — admins refresh the content, your schedule sends it automatically. Copy & customize takes a one-time snapshot you can edit freely.',
        placement: 'bottom',
      },
      {
        target: 'mm-templates-grid',
        action: 'showTemplatesTab',
        title: 'The company library',
        body: 'Ready-to-send newsletter designs published by your admins. Preview one first, then adopt it — either synced or as your own editable copy.',
        placement: 'top',
      },
    ],
  },
  {
    id: 'marketing-activity',
    version: 1,
    routes: ['/crm/my-marketing'],
    menuSection: 'Activity',
    title: 'Send Activity Log',
    autoStart: false, // replay-only: start it from the navbar tutorials menu
    requiresNewFeatures: false,
    steps: [
      {
        target: 'mm-activity-table',
        action: 'showActivityTab',
        title: 'Every send, one log',
        body: 'Newsletters, campaign automations, direct and bulk email, even SMS — each row shows the recipient, the subject, and whether it was opened or clicked.',
        placement: 'top',
      },
      {
        target: 'mm-activity-pagination',
        title: 'Dig back in time',
        body: 'Twenty-five sends per page — page through your full history to check what any realtor received and when.',
        placement: 'top',
      },
    ],
  },
  {
    id: 'marketing-admin',
    version: 1,
    routes: ['/crm/my-marketing'],
    menuSection: 'Admin',
    title: 'Marketing Admin Tools',
    autoStart: false, // replay-only: start it from the navbar tutorials menu
    audience: 'admins',
    requiresNewFeatures: false,
    steps: [
      {
        target: 'mm-view-as',
        title: 'Act on behalf of any LO',
        body: 'Pick a user to see this page exactly as they do — and set up newsletters FOR them. Anything you create or send belongs to them and goes out from their Gmail.',
        placement: 'bottom',
      },
      {
        target: 'mm-templates-grid',
        action: 'showTemplatesTab',
        title: 'You control this library',
        body: 'Publish templates in CRM Admin \u2192 Templates with category "newsletter" and they appear here for every LO. Refresh a synced template weekly and every subscribed newsletter sends the new content automatically.',
        placement: 'top',
      },
    ],
  },
  {
    id: 'newsletter-editor',
    version: 1,
    routes: ['/crm/my-marketing/newsletters/:newsletterId'],
    // Also listed on My Marketing under Newsletters: the page action opens the
    // user's most recent newsletter (or creates a draft), then the tour runs.
    menuAlso: [{ route: '/crm/my-marketing', section: 'Newsletters', action: 'openNewsletterForTour' }],
    title: 'Newsletter Editor',
    autoStart: true, // fires the first time a user opens ANY newsletter
    requiresNewFeatures: false,
    steps: [
      {
        target: 'nled-header',
        title: 'One newsletter, start to finish',
        body: 'Name it at the top — the status badge and next-send time live here too. Four sections below cover who gets it, what it says, and when it goes.',
        placement: 'bottom',
        waitForMs: 8000, // editor shows a full-page spinner while the newsletter loads
      },
      {
        target: 'nled-details',
        title: 'Internal details',
        body: 'The description is just for you. Replies always land in your Gmail inbox — set a different reply-to only if you want them somewhere else.',
        placement: 'bottom',
      },
      {
        target: 'nled-audience',
        title: 'Who receives it',
        body: 'Build the audience by contact type and tags — the live count updates as you refine. It re-resolves at every send, so new matching realtors join automatically and unsubscribes drop out.',
        placement: 'bottom',
      },
      {
        target: 'nled-content',
        title: 'What it says',
        body: 'Write your own content with merge fields like {{contact.firstName}} — every realtor gets a personal copy — or link a company template that auto-updates whenever admins refresh it. Your signature and an unsubscribe footer are added automatically.',
        placement: 'top',
      },
      {
        target: 'nled-schedule',
        title: 'When it goes out',
        body: 'Monthly, biweekly, weekly or one-time — the preview shows the next three sends so there are no surprises.',
        placement: 'top',
      },
      {
        target: 'nled-send-now',
        title: 'Off-schedule blast',
        body: 'Big rate move today? Send now saves your edits and fires this issue immediately without touching the recurring schedule.',
        placement: 'top',
      },
      {
        target: 'nled-actions',
        title: 'Before it counts',
        body: 'Preview renders the email, Send me a test delivers a merged copy to YOUR inbox, and Activate arms the schedule. Nothing sends to realtors until you activate or hit Send now.',
        placement: 'bottom',
      },
    ],
  },
  {
    id: 'deal-page',
    version: 1,
    routes: ['/crm/deals/:dealId'],
    title: 'Working a Deal',
    autoStart: true, // fires the first time a user opens ANY deal
    requiresNewFeatures: false,
    steps: [
      {
        target: 'deal-header',
        title: 'Your deal at a glance',
        body: 'The borrower\u2019s name and the deal\u2019s current stage are always pinned up here, next to their contact info and the header actions (Add Task, Edit, Arive).',
        placement: 'bottom',
        waitForMs: 8000, // deal + contact fetch can be slow on first load
      },
      {
        target: 'deal-log-activity',
        action: 'showActivityTab',
        title: 'Log every touch',
        body: 'This form is your core workflow. After each call or update: set the Stage, pick a Disposition and Next Step with a due date, jot your notes, then hit "Save & Log Activity". That one click updates the deal AND writes the history.',
        placement: 'right',
      },
      {
        target: 'deal-timeline',
        title: 'The full story',
        body: 'Every logged activity, stage change, email and text lands here \u2014 so you (or a teammate covering for you) can pick up exactly where things left off.',
        placement: 'right',
      },
      {
        target: 'deal-quick-actions',
        title: 'Reach out in one click',
        body: 'Call, text or email the borrower right from the deal. Do-Not-Contact flags disable the matching buttons automatically, so you can\u2019t slip up.',
        placement: 'left',
      },
      {
        target: 'deal-details',
        title: 'Loan vitals & parties',
        body: 'Loan amount, estimated closing, product, lender and loan number live here. The sections below hold borrower details, co-borrowers, property info and Related Parties (agents, title, escrow).',
        placement: 'left',
      },
      {
        target: 'deal-arive',
        title: 'Arive, in sync',
        body: 'This panel shows the deal\u2019s link to your Arive loan file \u2014 sync status, drift, and a shortcut to open it in Arive. Link the loan once and updates flow in automatically.',
        placement: 'left',
      },
      {
        target: 'deal-tasks',
        action: 'showTasksTab',
        title: 'Never miss a follow-up',
        body: 'The Tasks tab tracks every to-do for this deal. "New Task" creates a follow-up with a due date \u2014 it also shows up on your dashboard and in your daily plan.',
        placement: 'bottom',
      },
      {
        target: 'deal-campaigns',
        action: 'showCampaignsTab',
        title: 'Put nurture on autopilot',
        body: 'See every marketing campaign this borrower is enrolled in \u2014 and enroll them in a new one. Great for long-term nurture when a deal goes quiet.',
        placement: 'right',
      },
    ],
  },
  {
    id: 'contact-drawer',
    // v2: added the Log Activity quick action (contact-level dispositions).
    version: 2,
    // The drawer normally opens over the dashboard WITHOUT a route change, so
    // ContactDetailDrawer auto-starts this itself via useAutoTour(). autoStart
    // stays false so the route effect never races that trigger. The deep-link
    // route below keeps it in the navbar tutorials menu at /crm/contacts/:id;
    // the drawer also has its own in-header replay button.
    routes: ['/crm/contacts/:contactId'],
    title: 'Contact Workspace',
    autoStart: false,
    requiresNewFeatures: false,
    steps: [
      {
        target: 'contact-header',
        title: 'The contact workspace',
        body: 'Everything about this person in one drawer. Tap the star to favorite the contacts you work most \u2014 favorites get their own dashboard filter.',
        placement: 'bottom',
      },
      {
        target: 'contact-info',
        title: 'Reach them \u2014 safely',
        body: 'Email, phone, company and mailing address at a glance. If a red DNC badge shows here, outreach to that channel is blocked across the whole CRM.',
        placement: 'bottom',
      },
      {
        target: 'contact-tags',
        title: 'Tags do the heavy lifting',
        body: 'Tags organize your book AND drive automations \u2014 campaigns can auto-enroll or exit contacts when tags change. Add or remove them right here.',
        placement: 'left',
      },
      {
        target: 'contact-stats',
        title: 'Their history with you',
        body: 'Total deals, roles they\u2019ve played (borrower vs. related party) and lifetime funded value \u2014 a quick read on how important this relationship is.',
        placement: 'bottom',
      },
      {
        target: 'contact-quick-actions',
        title: 'Two clicks to anything',
        body: 'Call, text or email instantly, log an interaction, spin up a new deal, or add a follow-up task \u2014 all without leaving the drawer.',
        placement: 'bottom',
      },
      {
        target: 'contact-log-activity',
        title: 'Log any interaction',
        body: 'Called, texted, emailed or met with them \u2014 even outside the Hub? Record it here with a disposition and notes, no deal required. It lands on the Activity timeline, updates Last Interaction, and can trigger follow-up automations.',
        placement: 'bottom',
      },
      {
        target: 'contact-deals',
        action: 'showDealsTab',
        title: 'Every deal, both roles',
        body: 'All their deals in one place \u2014 as the borrower and as a related party (agent, co-borrower, etc.). Click any row to jump straight into that deal.',
        placement: 'left',
      },
      {
        target: 'contact-activity',
        action: 'showActivityTab',
        title: 'One unified timeline',
        body: 'Calls, emails, texts, notes and stage changes across ALL of their deals, merged into a single feed \u2014 the fastest way to catch up before you dial.',
        placement: 'left',
      },
      {
        target: 'contact-notes',
        action: 'showNotesTab',
        title: 'Notes about the person',
        body: 'Keep general notes here \u2014 preferences, family details, referral history. Deal-specific notes belong on the deal; these stay with the contact forever.',
        placement: 'left',
      },
      {
        target: 'contact-campaigns',
        action: 'showCampaignsTab',
        title: 'Keep in touch automatically',
        body: 'Enroll this contact in nurture or marketing campaigns and see everything they\u2019re already receiving. Set it once \u2014 the CRM keeps you in front of them.',
        placement: 'left',
      },
    ],
  },

  // ---------------------------------------------------------------------------
  // IMPORT STUDIO (all users — modal on Dashboard, My Deals and CRM Admin)
  // The Studio opens without a route change, so ImportStudioModal auto-starts
  // this itself via useAutoTour() on first open; autoStart stays false so the
  // route effect never races that trigger. Every step carries the
  // openImportForTour action (registered by all three host pages) so replaying
  // from the navbar tutorials menu opens the Studio first. The Studio header
  // also has its own inline replay button.
  // ---------------------------------------------------------------------------
  {
    id: 'import-studio',
    version: 1,
    routes: ['/crm/dashboard', '/crm', '/crm/my-deals', '/crm/admin'],
    title: 'Import Studio',
    autoStart: false,
    audience: 'all',
    requiresNewFeatures: false,
    steps: [
      {
        target: 'studio-stepper',
        action: 'openImportForTour',
        title: 'Welcome to the Import Studio',
        body: 'Your import workspace. Five quick stages: upload a file, match its columns to Origin, review duplicates, run the import, and see the results. Nothing is written until you press Start Import.',
        placement: 'bottom',
      },
      {
        target: 'studio-dropzone',
        action: 'openImportForTour',
        title: 'Any spreadsheet works',
        body: 'Drop in a CSV or Excel file (up to 10,000 rows). Multi-sheet workbooks let you pick the sheet, and the Studio reads the columns to tell whether you\u2019re importing contacts or deals \u2014 no matter which page you started from.',
        placement: 'bottom',
      },
      {
        target: 'studio-templates',
        action: 'openImportForTour',
        title: 'Templates, if you want a head start',
        body: 'Download a starter file with every column Origin understands plus an example row. Fill it in and it maps itself on upload \u2014 but any file works, even with extra columns.',
        placement: 'top',
      },
      {
        target: 'studio-stepper',
        action: 'openImportForTour',
        title: 'Nothing gets lost, nothing gets clobbered',
        body: 'After upload you\u2019ll match columns visually, then review duplicates with a per-row choice: keep the existing record or update it with the file\u2019s data \u2014 empty cells never erase anything. Columns without an Origin field are kept on each record as Imported Data.',
        placement: 'bottom',
      },
    ],
  },
];

const VALID_AUDIENCES = ['all', 'admins', 'non-admins'];

/**
 * "Admin" for tour-audience purposes = SuperAdmin or CRM Admin.
 * Deliberately NOT canAccessNewFeatures(): that helper is an admin-preview
 * gate that flips to `return true` at GA, which would silently widen every
 * admin-only tour to all users. This definition is frozen.
 */
export function isTourAdmin({ currentUser, SuperAdmin, Roles } = {}) {
  return (
    SuperAdmin === true ||
    currentUser?.SuperAdmin === true ||
    currentUser?.CRMRole === 'CRM Admin' ||
    (Array.isArray(Roles) && Roles.includes('CRM Admin')) ||
    (Array.isArray(currentUser?.Roles) && currentUser.Roles.includes('CRM Admin'))
  );
}

/** Is a tour with this audience visible to this (admin or not) user? */
export function audienceAllows(audience, isAdmin) {
  if (!audience || audience === 'all') return true;
  if (audience === 'admins') return !!isAdmin;
  if (audience === 'non-admins') return !isAdmin;
  console.warn(`FeatureTour: unknown audience "${audience}" — treating as 'all'.`);
  return true;
}

/**
 * Menu entries for the navbar tutorials dropdown on the given pathname:
 * tours whose routes match ("in place"), plus tours listed here via menuAlso.
 * Each entry: { tour, section, launch } where launch is
 *   { type: 'inplace' } — startTour directly
 *   { type: 'action', action } — run the page action (it navigates), then the
 *     provider starts the tour when its route matches
 *   { type: 'navigate', to } — provider pushes `to`, then starts the tour
 */
export function menuToursForPath(pathname) {
  const matches = (route) => matchPath(pathname, { path: route, exact: true });
  const entries = TOURS.filter((tour) => (tour.routes || []).some(matches)).map((tour) => ({
    tour,
    section: tour.menuSection || null,
    launch: { type: 'inplace' },
  }));
  const inPlaceIds = new Set(entries.map((e) => e.tour.id));

  TOURS.forEach((tour) => {
    if (inPlaceIds.has(tour.id)) return;
    (tour.menuAlso || []).forEach((placement) => {
      if (!placement || !placement.route || !matches(placement.route)) return;
      entries.push({
        tour,
        section: placement.section || tour.menuSection || null,
        launch: placement.action
          ? { type: 'action', action: placement.action }
          : { type: 'navigate', to: (tour.routes || [])[0] },
      });
    });
  });
  return entries;
}

/** All tours whose routes match the given pathname (registry order). */
export function toursForPath(pathname) {
  return TOURS.filter((tour) =>
    (tour.routes || []).some((route) => matchPath(pathname, { path: route, exact: true }))
  );
}

/** Look up a tour definition by id. */
export function getTourById(tourId) {
  return TOURS.find((tour) => tour.id === tourId) || null;
}

// Dev-time sanity checks so registry mistakes surface immediately in console.
if (process.env.NODE_ENV === 'development') {
  const ids = new Set();
  TOURS.forEach((tour) => {
    if (!tour.id || tour.id.includes('.')) {
      console.warn(`FeatureTour registry: tour id "${tour.id}" is invalid — ids must be kebab-case without dots (Firestore field path).`);
    }
    if (ids.has(tour.id)) {
      console.warn(`FeatureTour registry: duplicate tour id "${tour.id}".`);
    }
    ids.add(tour.id);
    if (!Array.isArray(tour.routes) || tour.routes.length === 0) {
      console.warn(`FeatureTour registry: tour "${tour.id}" has no routes.`);
    }
    if (!Number.isInteger(tour.version) || tour.version < 1) {
      console.warn(`FeatureTour registry: tour "${tour.id}" needs an integer version >= 1.`);
    }
    if (tour.audience != null && !VALID_AUDIENCES.includes(tour.audience)) {
      console.warn(
        `FeatureTour registry: tour "${tour.id}" has invalid audience "${tour.audience}" — use 'all', 'admins' or 'non-admins'.`
      );
    }
    (tour.steps || []).forEach((step, i) => {
      if (!step.target && !step.selector) {
        console.warn(`FeatureTour registry: tour "${tour.id}" step ${i} has neither target nor selector.`);
      }
    });
    (tour.menuAlso || []).forEach((placement, i) => {
      if (!placement || !placement.route) {
        console.warn(`FeatureTour registry: tour "${tour.id}" menuAlso[${i}] needs a route.`);
        return;
      }
      const everyRouteHasParam = (tour.routes || []).every((r) => r.includes(':'));
      if (!placement.action && everyRouteHasParam) {
        console.warn(
          `FeatureTour registry: tour "${tour.id}" menuAlso[${i}] needs an action — all of its own routes have :params, so the engine can't navigate there by itself.`
        );
      }
    });
  });
}

export default TOURS;
